import { Component } from "react"
import PropTypes from "prop-types"
import { combineReducers, createStore, applyMiddleware } from "redux"
import { Provider } from "react-redux"
import { thunk, withExtraArgument } from "redux-thunk"
import * as actions from "./actions"
import reducers from "./reducers"

import AppletComposerUI from "./applet_composer_ui"
import OpenLinksInNewWindow from "shared/components/open_links_in_new_window"
import Spinner from "shared/components/spinner"

import { loadPrivateServices, loadStaticApplet, loadTags } from "./gql"

import { serviceComposerShape } from "partners/components/shared/shared_prop_types"

import styles from "./applet_composer.module.scss"

export default class StaticAppletComposer extends Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: true,
      create: props.create || props.appletId === null,
      tagOptions: [],
    }

    const combinedReducers = combineReducers(reducers)
    this.store = createStore(combinedReducers, applyMiddleware(thunk))
  }

  componentDidMount() {
    const { create, fromAppletTemplate, fromParams, isMaker, preloadedServices } = this.props

    const loading = [
      loadPrivateServices().then(privateServices => {
        this.store.dispatch(actions.loadServicesAction(privateServices.concat(preloadedServices)))
      }),
    ]

    let appletPromise = async () => ({})
    if (this.props.appletId) {
      appletPromise = loadStaticApplet(this.props.appletId)
    } else {
      appletPromise = loadTags()
    }

    Promise.all(loading)
      .then(() => this.setState({ loading: false }))
      .then(async () => {
        const { applet: initialApplet = {}, tags = [] } = await appletPromise
        this.setState({ tagOptions: tags })

        // Note: StaticAppletComposer relies on initApplet.id === null to create/edit
        if (initialApplet?.id) {
          // When copying, reset published and archived because this applet is
          // a template for a new, unpublished applet.
          const applet = create
            ? { ...initialApplet, id: null, published: false, archived: false }
            : initialApplet
          this.store.dispatch(actions.loadAppletDependencies(applet, isMaker))
        } else if (fromAppletTemplate) {
          this.store.dispatch(actions.loadAppletDependencies(fromAppletTemplate, isMaker))
        } else if (fromParams) {
          this.store.dispatch(
            actions.loadTandasAsTemplate(fromParams.triggerModuleName, fromParams.actionModuleName, isMaker)
          )
        } else {
          this.store.dispatch(actions.newApplet(isMaker))
        }
      })
  }

  render() {
    const {
      appletFilterTransformTemplate,
      contextChannelId,
      fromParams,
      isMaker,
      canDelay,
      canFilter,
      canTag,
      canQuery,
      canMultiAction,
      appletsUrl,
      appletShowUrl,
      docsUrl,
      proPlusSubscriptionUrl,
      proSubscriptionUrl,
      createOrUpdateAppletUrl,
    } = this.props

    return (
      <div>
        <div className={styles["connection-header"]}>
          {this.props.create && (
            <div className={styles["header-content"]}>
              <div>
                <h1>Create a new Applet</h1>
              </div>
            </div>
          )}
        </div>
        {this.state.loading ? (
          <div className="spinner">
            <Spinner />
          </div>
        ) : (
          <OpenLinksInNewWindow>
            <Provider store={this.store}>
              <AppletComposerUI
                appletFilterTransformTemplate={appletFilterTransformTemplate}
                contextChannelId={contextChannelId}
                isMaker={isMaker}
                canDelay={canDelay}
                canFilter={canFilter}
                canTag={canTag}
                canQuery={canQuery}
                canMultiAction={canMultiAction}
                isSuggested={!!fromParams}
                appletsUrl={appletsUrl}
                appletShowUrl={appletShowUrl}
                docsUrl={docsUrl}
                proPlusSubscriptionUrl={proPlusSubscriptionUrl}
                proSubscriptionUrl={proSubscriptionUrl}
                createOrUpdateAppletUrl={createOrUpdateAppletUrl}
                tagOptions={this.state.tagOptions}
              />
            </Provider>
          </OpenLinksInNewWindow>
        )}
      </div>
    )
  }
}

StaticAppletComposer.propTypes = {
  create: PropTypes.bool,
  appletId: PropTypes.string,
  appletFilterTransformTemplate: PropTypes.string.isRequired,
  contextChannelId: PropTypes.string.isRequired,
  docsUrl: PropTypes.string,
  fromAppletTemplate: PropTypes.object,
  fromParams: PropTypes.shape({
    triggerModuleName: PropTypes.string.isRequired,
    actionModuleName: PropTypes.string.isRequired,
  }),
  isMaker: PropTypes.bool.isRequired,
  canDelay: PropTypes.bool.isRequired,
  canFilter: PropTypes.bool.isRequired,
  canTag: PropTypes.bool.isRequired,
  canQuery: PropTypes.bool.isRequired,
  canMultiAction: PropTypes.bool.isRequired,
  appletsUrl: PropTypes.string.isRequired,
  appletShowUrl: PropTypes.string.isRequired,
  proPlusSubscriptionUrl: PropTypes.string.isRequired,
  proSubscriptionUrl: PropTypes.string.isRequired,
  createOrUpdateAppletUrl: PropTypes.string.isRequired,
  preloadedServices: PropTypes.arrayOf(serviceComposerShape.isRequired),
}
